import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Target,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    Loader2,
    Bell,
    BellOff,
    Settings,
    Activity,
    Clock,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useOpenPositions } from '@/api/positions/use-positions';
import { useNavigate } from 'react-router-dom';
import { useTraderPermissions } from '@/hooks/useTraderPermissions';
import { TraderPermissionGate } from '@/components/TraderPermissionGate';
import { ModulePermission, PermissionAction } from '@/types/permissions';

interface StopLossTakeProfitWidgetProps {
    tradingAccountId?: string;
    className?: string;
    alertThresholds?: {
        nearThreshold?: number; // Percentage threshold for "near" alerts
        criticalThreshold?: number; // Percentage threshold for critical alerts
    };
}

const StopLossTakeProfitWidget: React.FC<StopLossTakeProfitWidgetProps> = ({
    tradingAccountId,
    className,
    alertThresholds = {
        nearThreshold: 2, // 2% threshold for "near" alerts
        criticalThreshold: 0.5, // 0.5% threshold for critical alerts
    },
}) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [alertsEnabled, setAlertsEnabled] = useState(true);
    const [lastAlertTime, setLastAlertTime] = useState<Date | null>(null);

    const { data: positionsData, isLoading, refetch } = useOpenPositions(tradingAccountId);

    const positions = positionsData?.data || [];

    // Calculate SL/TP metrics
    const slTpMetrics = {
        totalPositions: positions.length,
        positionsWithSL: positions.filter(pos => pos.stop_loss_price).length,
        positionsWithTP: positions.filter(pos => pos.take_profit_price).length,
        positionsNearSL: positions.filter(pos => {
            if (!pos.stop_loss_price || !pos.current_price) return false;
            const distance = Math.abs(pos.current_price - pos.stop_loss_price) / pos.current_price;
            return distance <= alertThresholds.nearThreshold! / 100;
        }).length,
        positionsNearTP: positions.filter(pos => {
            if (!pos.stop_loss_price || !pos.current_price) return false;
            const distance =
                Math.abs(pos.current_price - pos.take_profit_price) / pos.current_price;
            return distance <= alertThresholds.nearThreshold! / 100;
        }).length,
        positionsCriticalSL: positions.filter(pos => {
            if (!pos.stop_loss_price || !pos.current_price) return false;
            const distance = Math.abs(pos.current_price - pos.stop_loss_price) / pos.current_price;
            return distance <= alertThresholds.criticalThreshold! / 100;
        }).length,
        positionsCriticalTP: positions.filter(pos => {
            if (!pos.take_profit_price || !pos.current_price) return false;
            const distance =
                Math.abs(pos.current_price - pos.take_profit_price) / pos.current_price;
            return distance <= alertThresholds.criticalThreshold! / 100;
        }).length,
    };

    // Generate alerts
    const alerts = [];

    if (alertsEnabled) {
        // Critical SL alerts
        if (slTpMetrics.positionsCriticalSL > 0) {
            alerts.push({
                type: 'critical_sl',
                severity: 'critical',
                title: 'Critical Stop Loss',
                message: `${slTpMetrics.positionsCriticalSL} position(s) are very close to stop loss trigger.`,
                icon: <AlertTriangle className="h-4 w-4" />,
            });
        }

        // Critical TP alerts
        if (slTpMetrics.positionsCriticalTP > 0) {
            alerts.push({
                type: 'critical_tp',
                severity: 'high',
                title: 'Critical Take Profit',
                message: `${slTpMetrics.positionsCriticalTP} position(s) are very close to take profit trigger.`,
                icon: <Target className="h-4 w-4" />,
            });
        }

        // Near SL alerts
        if (slTpMetrics.positionsNearSL > 0 && slTpMetrics.positionsCriticalSL === 0) {
            alerts.push({
                type: 'near_sl',
                severity: 'medium',
                title: 'Near Stop Loss',
                message: `${slTpMetrics.positionsNearSL} position(s) are approaching stop loss level.`,
                icon: <TrendingDown className="h-4 w-4" />,
            });
        }

        // Near TP alerts
        if (slTpMetrics.positionsNearTP > 0 && slTpMetrics.positionsCriticalTP === 0) {
            alerts.push({
                type: 'near_tp',
                severity: 'low',
                title: 'Near Take Profit',
                message: `${slTpMetrics.positionsNearTP} position(s) are approaching take profit level.`,
                icon: <TrendingUp className="h-4 w-4" />,
            });
        }

        // No protection alerts
        const positionsWithoutSL = slTpMetrics.totalPositions - slTpMetrics.positionsWithSL;
        const positionsWithoutTP = slTpMetrics.totalPositions - slTpMetrics.positionsWithTP;

        if (positionsWithoutSL > 0) {
            alerts.push({
                type: 'no_sl',
                severity: 'medium',
                title: 'No Stop Loss Protection',
                message: `${positionsWithoutSL} position(s) have no stop loss protection.`,
                icon: <AlertTriangle className="h-4 w-4" />,
            });
        }

        if (positionsWithoutTP > 0) {
            alerts.push({
                type: 'no_tp',
                severity: 'low',
                title: 'No Take Profit Set',
                message: `${positionsWithoutTP} position(s) have no take profit target.`,
                icon: <Target className="h-4 w-4" />,
            });
        }
    }

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return 'border-red-500 bg-red-50 dark:bg-red-950/20';
            case 'high':
                return 'border-orange-500 bg-orange-50 dark:bg-orange-950/20';
            case 'medium':
                return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20';
            case 'low':
                return 'border-blue-500 bg-blue-50 dark:bg-blue-950/20';
            default:
                return 'border-gray-500 bg-gray-50 dark:bg-gray-950/20';
        }
    };

    const getSeverityTextColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return 'text-red-700 dark:text-red-300';
            case 'high':
                return 'text-orange-700 dark:text-orange-300';
            case 'medium':
                return 'text-yellow-700 dark:text-yellow-300';
            case 'low':
                return 'text-blue-700 dark:text-blue-300';
            default:
                return 'text-gray-700 dark:text-gray-300';
        }
    };

    const formatNumber = (num: number, decimals: number = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(num);
    };

    const handleViewPositions = () => {
        navigate('/trade');
    };

    const handleToggleAlerts = () => {
        setAlertsEnabled(!alertsEnabled);
        enqueueSnackbar(`SL/TP alerts ${alertsEnabled ? 'disabled' : 'enabled'}`, {
            variant: alertsEnabled ? 'warning' : 'success',
        });
    };

    // Show alert notifications
    useEffect(() => {
        if (alertsEnabled && alerts.length > 0) {
            const now = new Date();
            const shouldShowAlert =
                !lastAlertTime || now.getTime() - lastAlertTime.getTime() > 30000; // 30 seconds

            if (shouldShowAlert) {
                const criticalAlerts = alerts.filter(alert => alert.severity === 'critical');
                if (criticalAlerts.length > 0) {
                    enqueueSnackbar(criticalAlerts[0].message, {
                        variant: 'error',
                        autoHideDuration: 10000,
                    });
                    setLastAlertTime(now);
                }
            }
        }
    }, [alerts, alertsEnabled, lastAlertTime, enqueueSnackbar]);

    if (!tradingAccountId) {
        return (
            <Card className={className}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        SL/TP Monitoring
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center text-muted-foreground py-8">
                        <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Select a trading account to view SL/TP status</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={className}>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        SL/TP Monitoring
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleToggleAlerts}
                            className="h-8 w-8 p-0"
                        >
                            {alertsEnabled ? (
                                <Bell className="h-4 w-4" />
                            ) : (
                                <BellOff className="h-4 w-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleViewPositions}
                            className="h-8 w-8 p-0"
                        >
                            <Activity className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Alert Status */}
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2">
                                {alertsEnabled ? (
                                    <Bell className="h-4 w-4 text-green-500" />
                                ) : (
                                    <BellOff className="h-4 w-4 text-gray-500" />
                                )}
                                <span className="text-sm font-medium">
                                    Alerts {alertsEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                            </div>
                            <Badge variant={alertsEnabled ? 'default' : 'secondary'}>
                                {alerts.length} Active
                            </Badge>
                        </div>

                        {/* Protection Summary */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="text-center p-3 bg-muted rounded-lg">
                                <div className="text-lg font-bold text-red-600">
                                    {slTpMetrics.positionsWithSL}
                                </div>
                                <div className="text-xs text-muted-foreground">Stop Loss</div>
                            </div>
                            <div className="text-center p-3 bg-muted rounded-lg">
                                <div className="text-lg font-bold text-green-600">
                                    {slTpMetrics.positionsWithTP}
                                </div>
                                <div className="text-xs text-muted-foreground">Take Profit</div>
                            </div>
                        </div>

                        {/* Monitoring Status */}
                        {(slTpMetrics.positionsNearSL > 0 || slTpMetrics.positionsNearTP > 0) && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Monitoring
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                        <div className="text-sm font-bold text-yellow-700 dark:text-yellow-300">
                                            {slTpMetrics.positionsNearSL}
                                        </div>
                                        <div className="text-xs text-yellow-600 dark:text-yellow-400">
                                            Near SL
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                                        <div className="text-sm font-bold text-green-700 dark:text-green-300">
                                            {slTpMetrics.positionsNearTP}
                                        </div>
                                        <div className="text-xs text-green-600 dark:text-green-400">
                                            Near TP
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Alerts List */}
                        {alerts.length === 0 ? (
                            <div className="text-center text-muted-foreground py-6">
                                <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No SL/TP alerts</p>
                                <p className="text-xs">
                                    All positions are within normal parameters
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {alerts.map((alert, index) => (
                                    <Alert
                                        key={index}
                                        className={`${getSeverityColor(alert.severity)} border`}
                                    >
                                        <div className={`${getSeverityTextColor(alert.severity)}`}>
                                            {alert.icon}
                                        </div>
                                        <AlertDescription
                                            className={`${getSeverityTextColor(alert.severity)}`}
                                        >
                                            <div className="font-medium">{alert.title}</div>
                                            <div className="text-sm mt-1">{alert.message}</div>
                                        </AlertDescription>
                                    </Alert>
                                ))}
                            </div>
                        )}

                        {/* Protection Coverage */}
                        {slTpMetrics.totalPositions > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Protection Coverage
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Stop Loss Coverage</span>
                                        <span className="font-medium">
                                            {Math.round(
                                                (slTpMetrics.positionsWithSL /
                                                    slTpMetrics.totalPositions) *
                                                    100
                                            )}
                                            %
                                        </span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className="bg-red-500 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${(slTpMetrics.positionsWithSL / slTpMetrics.totalPositions) * 100}%`,
                                            }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Take Profit Coverage</span>
                                        <span className="font-medium">
                                            {Math.round(
                                                (slTpMetrics.positionsWithTP /
                                                    slTpMetrics.totalPositions) *
                                                    100
                                            )}
                                            %
                                        </span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${(slTpMetrics.positionsWithTP / slTpMetrics.totalPositions) * 100}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick Actions */}
                        {alerts.length > 0 && (
                            <div className="pt-3 border-t">
                                <TraderPermissionGate
                                    module={ModulePermission.TRADING}
                                    action={PermissionAction.VIEW}
                                >
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleViewPositions}
                                        className="w-full"
                                    >
                                        <Activity className="h-4 w-4 mr-2" />
                                        Review Positions
                                    </Button>
                                </TraderPermissionGate>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default StopLossTakeProfitWidget;
